import React from 'react';
import styled, { keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';
// @ts-ignore
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { FaRobot, FaFileExcel, FaCopy } from 'react-icons/fa';
import * as XLSX from 'xlsx';

interface IChatBubbleProps {
  text: string;
  isUser: boolean;
  citations?: { snippet: string; source: string }[];
  statusCode?: string;
  statusMessage?: string;
  rawData?: any[]; // For Excel Download
  column_mapping?: { [key: string]: string };
  uiMode?: string;
  sqlQuery?: string;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const BubbleRow = styled.div<{ $isUser: boolean }>`
  display: flex;
  justify-content: ${(props) => (props.$isUser ? 'flex-end' : 'flex-start')};
  gap: 10px;
  align-items: flex-end;
  margin-bottom: 12px;
  animation: ${fadeIn} 0.2s ease-out;
  padding: 0 5px;
`;

const Avatar = styled.div<{ $isUser: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${(props) => (props.$isUser ? '#4A90E2' : '#7B61FF')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 13px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`;

const BubbleContainer = styled.div<{ $isUser: boolean }>`
  margin: ${(props) => (props.$isUser ? '0px 0px 0px 50px' : '0px 50px 0px 0px')};
  padding: 10px 14px;
  border-radius: 16px;
  border-top-left-radius: ${(props) => (props.$isUser ? '16px' : '2px')};
  border-bottom-right-radius: ${(props) => (props.$isUser ? '2px' : '16px')};

  /* Premium Light Theme for Clarity */
  background: ${(props) => (props.$isUser ? '#0072ce' : '#ffffff')};
  color: ${(props) => (props.$isUser ? '#ffffff' : '#1f2937')};

  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  line-height: 1.5;
  position: relative;

  /* Text handling */
  word-break: normal; /* Use default break rules */
  overflow-wrap: break-word; /* Only break long words if they overflow */
  word-wrap: break-word; /* Legacy support */

  /* Markdown Styling Override */
  p {
    margin: 0;
    /* Ensure text color inherits correctly */
    color: inherit;
  }
  strong {
    font-weight: 600;
  }
  a {
    color: ${(props) => (props.$isUser ? '#E0F2FE' : '#0072CE')};
    text-decoration: underline;
  }
  ul,
  ol {
    margin: 4px 0;
    padding-left: 18px;
  }
  code {
    background: ${(props) => (props.$isUser ? 'rgba(255,255,255,0.15)' : '#f3f4f6')};
    padding: 2px 4px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 12px;
  }
  pre {
    background: ${(props) => (props.$isUser ? 'rgba(0,0,0,0.15)' : '#1f2937')};
    color: ${(props) => (props.$isUser ? '#ffffff' : '#f8f9fa')};
    padding: 8px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 6px 0;
  }
  /* Table styles removed here, handled by custom components below */
`;

// --- Custom Table Components (for clearer rendering) ---
const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
  font-size: 13px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  /* Force text color for readability inside bubbles */
  color: #374151;
`;

const StyledTh = styled.th`
  background-color: #f3f4f6;
  color: #1f2937;
  font-weight: 700;
  padding: 10px 12px;
  text-align: left;
  border-bottom: 2px solid #e5e7eb;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StyledTd = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
  color: #4b5563;
  font-size: 13px;
  line-height: 1.5;
`;

const StyledTr = styled.tr`
  &:last-child td {
    border-bottom: none;
  }
  &:hover {
    background-color: #f9fafb;
  }
`;

const MarkdownComponents = {
  table: StyledTable,
  th: StyledTh,
  td: StyledTd,
  tr: StyledTr,
};

const StatusMessage = styled.div`
  font-size: 11px;
  color: #ef4444;
  margin-bottom: 4px;
  font-weight: 600;
`;

const ActionArea = styled.div`
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #dcfce7;
  }

  svg {
    font-size: 14px;
  }
`;

// Specific style for SQL button
const SqlButton = styled(ActionButton)`
  background-color: #eff6ff;
  color: #1e40af;
  border-color: #bfdbfe;

  &:hover {
    background-color: #dbeafe;
  }
`;

export default function ChatBubble({
  text,
  isUser,
  citations,
  statusCode,
  statusMessage,
  rawData,
  column_mapping,
  uiMode,
  sqlQuery,
}: IChatBubbleProps) {
  const isError = statusCode && statusCode !== 'SUCCESS';

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      alert('내용이 클립보드에 복사되었습니다.');
    } catch (err) {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('내용이 클립보드에 복사되었습니다.');
    }
  };

  const handleDownloadExcel = () => {
    if (!rawData || rawData.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    try {
      // 1. Prepare Data with Localized Headers
      let dataToExport = rawData;
      if (column_mapping && Object.keys(column_mapping).length > 0) {
        dataToExport = rawData.map((row) => {
          const newRow: any = {};
          Object.keys(row).forEach((key) => {
            const newKey = column_mapping[key] || key;
            newRow[newKey] = row[key];
          });
          return newRow;
        });
      }

      // 2. Create Worksheet
      const ws = XLSX.utils.json_to_sheet(dataToExport);

      // 2. Create Workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');

      // 3. Generate File Name (with timestamp)
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `wakeup_data_${timestamp}.xlsx`;

      // 4. Download
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    }
  };

  const handleCopyQuery = async () => {
    if (!sqlQuery) return;

    try {
      await navigator.clipboard.writeText(sqlQuery);
      alert('SQL 쿼리가 클립보드에 복사되었습니다.');
    } catch (err) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = sqlQuery;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (successful) {
          alert('SQL 쿼리가 클립보드에 복사되었습니다.');
        } else {
          throw new Error('Fallback failed');
        }
      } catch (fallbackErr) {
        prompt('클립보드 복사에 실패했습니다. 아래 텍스트를 직접 복사해주세요:', sqlQuery);
      }
    }
  };

  let isAdmin = false;
  if (typeof window !== 'undefined') {
    try {
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        isAdmin = (userInfo.type || '').toUpperCase() === 'ADMIN';
      }
    } catch (e) {}
  }

  return (
    <BubbleRow $isUser={isUser}>
      {/* Bot Avatar */}
      {!isUser && (
        <Avatar $isUser={false}>
          <FaRobot />
        </Avatar>
      )}

      <BubbleContainer $isUser={isUser}>
        {isError && <StatusMessage>⚠️ {statusMessage || '요청 처리 중 문제가 발생했습니다.'}</StatusMessage>}

        <ReactMarkdown remarkPlugins={[remarkBreaks, remarkGfm]} components={MarkdownComponents}>
          {text}
        </ReactMarkdown>

        {/* Helper Actions */}
        {!isUser && (
          <ActionArea>
            {/* Excel Download */}
            {rawData && rawData.length > 0 && (
              <ActionButton onClick={handleDownloadExcel}>
                <FaFileExcel />
                엑셀로 다운로드
              </ActionButton>
            )}

            {/* Copy Text Button (For All Users) */}
            <ActionButton onClick={handleCopyText}>
              <FaCopy />
              내용 복사
            </ActionButton>

            {/* Copy SQL Query (Admin Only) */}
            {isAdmin && sqlQuery && (
              <SqlButton onClick={handleCopyQuery}>
                <FaCopy />
                쿼리 복사하기
              </SqlButton>
            )}
          </ActionArea>
        )}
      </BubbleContainer>
    </BubbleRow>
  );
}
